import React from "react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { FieldComponentProps, isFieldApi } from "~/lib/form";
import { cn } from "~/shared/lib/utils";
import { t } from "@lingui/core/macro";

interface ValidatedDatePickerProps extends FieldComponentProps {
  minAge?: number;
  maxAge?: number;
  showHint?: boolean;
}

type DateSegments = {
  year: string;
  month: string;
  day: string;
};

const emptySegments: DateSegments = {
  year: "",
  month: "",
  day: "",
};

const parseLocalDateString = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateValue = (value: Date | string | undefined | null): string => {
  if (value === undefined || value === null || value === "") return "";
  const date =
    typeof value === "string" ? (parseLocalDateString(value) ?? new Date(value)) : value;
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateSegments = (value: Date | string | undefined | null): DateSegments => {
  const formatted = formatDateValue(value);
  if (!formatted) return { ...emptySegments };
  const [year, month, day] = formatted.split("-");
  if (!year || !month || !day) return { ...emptySegments };
  return { year, month, day };
};

const isCompleteSegments = (segments: DateSegments) =>
  segments.year.length === 4 && segments.month.length === 2 && segments.day.length === 2;

const isValidDateParts = (year: number, month: number, day: number) => {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return false;
  }
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
  );
};

const splitDateDigits = (value: string): DateSegments => ({
  year: value.slice(0, 4),
  month: value.slice(4, 6),
  day: value.slice(6, 8),
});

export const ValidatedDatePicker: React.FC<ValidatedDatePickerProps> = (props) => {
  const { field, label, minAge = 13, maxAge = 120, showHint = true, className } = props;

  const fieldApi = isFieldApi(field) ? field : null;
  const fieldValue = fieldApi?.state.value;

  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - minAge,
    today.getMonth(),
    today.getDate(),
  );
  const minDate = new Date(
    today.getFullYear() - maxAge,
    today.getMonth(),
    today.getDate(),
  );
  const rangeHint = `Enter a date between ${formatDateValue(minDate)} and ${formatDateValue(
    maxDate,
  )}.`;

  const [segments, setSegments] = React.useState<DateSegments>(() =>
    parseDateSegments(fieldValue),
  );
  const [internalError, setInternalError] = React.useState<string | null>(null);
  const lastValueRef = React.useRef<string>(formatDateValue(fieldValue));
  const yearRef = React.useRef<HTMLInputElement>(null);
  const monthRef = React.useRef<HTMLInputElement>(null);
  const dayRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const normalized = formatDateValue(fieldValue);
    if (normalized === lastValueRef.current) return;
    lastValueRef.current = normalized;
    // Sync external field updates into the segmented inputs.
    // oxlint-disable-next-line eslint-react-js/hooks-extra/no-direct-set-state-in-use-effect -- state sync with external source; useRef gating prevents loops.
    // oxlint-disable-next-line eslint-react-js/hooks-extra/no-direct-set-state-in-use-effect -- syncing external field value into internal segmented inputs on prop change.
    setInternalError(null);
    // oxlint-disable-next-line eslint-react-js/hooks-extra/no-direct-set-state-in-use-effect -- syncing external field value into internal segmented inputs on prop change.
    setSegments(parseDateSegments(fieldValue));
  }, [fieldValue]);

  if (!fieldApi) {
    return null;
  }

  const fieldName = fieldApi.name;
  const meta = fieldApi.state.meta;
  const isSubmitting = fieldApi.form.state.isSubmitting;
  const handleChange = fieldApi.handleChange;
  const handleBlur = fieldApi.handleBlur;

  const inputId = `${fieldName}-date`;
  const labelId = `${inputId}-label`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-errors`;

  const commitValue = (value: string | undefined) => {
    const normalized = value ?? "";
    if (normalized === lastValueRef.current) return;
    lastValueRef.current = normalized;
    handleChange(value);
  };

  const commitFromSegments = (nextSegments: DateSegments) => {
    if (!isCompleteSegments(nextSegments)) {
      setInternalError(null);
      commitValue(undefined);
      return;
    }

    const year = Number(nextSegments.year);
    const month = Number(nextSegments.month);
    const day = Number(nextSegments.day);

    if (!isValidDateParts(year, month, day)) {
      setInternalError("Invalid date");
      commitValue(undefined);
      return;
    }

    setInternalError(null);
    commitValue(`${nextSegments.year}-${nextSegments.month}-${nextSegments.day}`);
  };

  const moveFocus = (target: React.RefObject<HTMLInputElement | null>) => {
    if (!target.current) return;
    target.current.focus();
    target.current.setSelectionRange(
      target.current.value.length,
      target.current.value.length,
    );
  };

  const handleSegmentChange =
    (
      segment: keyof DateSegments,
      maxLength: number,
      nextRef?: React.RefObject<HTMLInputElement | null>,
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const digits = event.target.value.replace(/\D/g, "");
      const previousLength = segments[segment].length;
      const nextSegments: DateSegments = {
        ...segments,
        [segment]: digits.slice(0, maxLength),
      };

      setSegments(nextSegments);
      setInternalError(null);
      commitFromSegments(nextSegments);

      if (digits.length === maxLength && previousLength < maxLength && nextRef) {
        moveFocus(nextRef);
      }
    };

  const handleSegmentPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 8);
    if (digits.length < 6) return;
    event.preventDefault();
    const nextSegments = splitDateDigits(digits);

    setSegments(nextSegments);
    setInternalError(null);
    commitFromSegments(nextSegments);

    if (nextSegments.year.length < 4) {
      moveFocus(yearRef);
      return;
    }
    if (nextSegments.month.length < 2) {
      moveFocus(monthRef);
      return;
    }
    moveFocus(dayRef);
  };

  const handleSegmentKeyDown =
    (segment: keyof DateSegments, prevRef?: React.RefObject<HTMLInputElement | null>) =>
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Backspace") return;
      if (segments[segment].length > 0) return;
      if (prevRef) {
        event.preventDefault();
        moveFocus(prevRef);
      }
    };

  const handleGroupBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    handleBlur();
  };

  const errorMessages =
    meta.isTouched && (internalError || meta.errors.length > 0)
      ? [internalError, ...meta.errors].filter(Boolean)
      : [];
  const hasErrors = Boolean(internalError) || meta.errors.length > 0;
  const showErrors = errorMessages.length > 0;
  const describedBy = showErrors
    ? showHint
      ? `${hintId} ${errorId}`
      : errorId
    : showHint
      ? hintId
      : undefined;

  return (
    <div className={cn("space-y-2", className)}>
      <Label id={labelId} htmlFor={`${inputId}-year`}>
        {label}
      </Label>
      <div
        className="flex items-center gap-2"
        role="group"
        aria-labelledby={labelId}
        onBlur={handleGroupBlur}
      >
        <Input
          ref={yearRef}
          id={`${inputId}-year`}
          type="text"
          inputMode="numeric"
          autoComplete="bday-year"
          pattern="[0-9]*"
          maxLength={4}
          placeholder={t`YYYY`}
          value={segments.year}
          onChange={handleSegmentChange("year", 4, monthRef)}
          onKeyDown={handleSegmentKeyDown("year")}
          onPaste={handleSegmentPaste}
          disabled={isSubmitting}
          aria-invalid={hasErrors}
          aria-describedby={describedBy}
          aria-label={t`Year`}
          className="w-20 text-center tabular-nums"
        />
        <span className="text-muted-foreground">/</span>
        <Input
          ref={monthRef}
          id={`${inputId}-month`}
          type="text"
          inputMode="numeric"
          autoComplete="bday-month"
          pattern="[0-9]*"
          maxLength={2}
          placeholder={t`MM`}
          value={segments.month}
          onChange={handleSegmentChange("month", 2, dayRef)}
          onKeyDown={handleSegmentKeyDown("month", yearRef)}
          onPaste={handleSegmentPaste}
          disabled={isSubmitting}
          aria-invalid={hasErrors}
          aria-describedby={describedBy}
          aria-label={t`Month`}
          className="w-14 text-center tabular-nums"
        />
        <span className="text-muted-foreground">/</span>
        <Input
          ref={dayRef}
          id={`${inputId}-day`}
          type="text"
          inputMode="numeric"
          autoComplete="bday-day"
          pattern="[0-9]*"
          maxLength={2}
          placeholder={t`DD`}
          value={segments.day}
          onChange={handleSegmentChange("day", 2)}
          onKeyDown={handleSegmentKeyDown("day", monthRef)}
          onPaste={handleSegmentPaste}
          disabled={isSubmitting}
          aria-invalid={hasErrors}
          aria-describedby={describedBy}
          aria-label={t`Day`}
          className="w-14 text-center tabular-nums"
        />
      </div>
      {showHint && (
        <span id={hintId} className="sr-only">
          {rangeHint}
        </span>
      )}
      {errorMessages.length > 0 && (
        <div
          id={errorId}
          role="alert"
          aria-live="polite"
          className="text-destructive text-sm font-medium"
        >
          {label}: {errorMessages.join(", ")}
        </div>
      )}
    </div>
  );
};
